// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useState, useEffect, useCallback } from 'react';
import { listApprovals, submitReviewDecision } from '../services/hitlService';
import { fetchPlanFile } from '../services/planService';
import type { HitlApproval, HitlApprovalState, HitlReviewSummary, PlanFile } from '../types';
import { HITL_SITE_ID, HITL_DC_SITE_LABEL, HITL_APPROVAL_TYPE } from '../utils/constants';

/**
 * A HITL approval, adapted to the shape the plan detail tabs consume.
 * `storeKey` is the approval's `attributes.objectKey` — the Object Store
 * key the actual plan narration file was uploaded under.
 */
export interface ApprovalPlan {
  approvalId: string;
  reviewId: string;
  storeKey: string;
  dc_site_id: string;
  date: string;
  status: HitlApprovalState;
  review_date?: string;
  reviewComment?: string;
  reviews: HitlReviewSummary[];
  attributes: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

function toApprovalPlan(approval: HitlApproval): ApprovalPlan {
  const attributes = approval.attributes ?? {};
  const objectKey = attributes.objectKey;
  const review = approval.reviews[0];
  // HITL nests the reviewer's comment under `review.attributes.comment`
  // rather than a flat `comment` field.
  const reviewComment = review?.attributes?.comment;
  return {
    approvalId: approval.approvalId,
    reviewId: review?.reviewId ?? '',
    storeKey: typeof objectKey === 'string' ? objectKey : '',
    dc_site_id: HITL_DC_SITE_LABEL,
    date: (approval.createdAt ?? '').slice(0, 10),
    status: approval.state,
    review_date: approval.state !== 'PENDING' ? (approval.updatedAt ?? undefined) : undefined,
    reviewComment:
      approval.state !== 'PENDING' && typeof reviewComment === 'string' ? reviewComment : undefined,
    reviews: approval.reviews,
    attributes,
    createdAt: approval.createdAt ?? '',
    updatedAt: approval.updatedAt ?? '',
  };
}

interface UsePlanDetailResult {
  plan: ApprovalPlan | null;
  planFile: PlanFile | null;
  loading: boolean;
  error: string | null;
  updateStatus: (_status: 'APPROVED' | 'REJECTED', _comment?: string) => Promise<void>;
}

export function usePlanDetail(approvalId: string | undefined): UsePlanDetailResult {
  const [plan, setPlan] = useState<ApprovalPlan | null>(null);
  const [planFile, setPlanFile] = useState<PlanFile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!approvalId) return;
    try {
      setLoading(true);
      setError(null);

      // No "get approval by ID" endpoint exists — fetch the (site + type)
      // filtered list and find the matching approval, same as the list screen.
      const approvals = await listApprovals({
        siteId: HITL_SITE_ID,
        approvalType: HITL_APPROVAL_TYPE,
      });
      const approval = approvals.find(a => a.approvalId === approvalId);
      if (!approval) {
        setPlan(null);
        setPlanFile(null);
        setError('Approval not found');
        return;
      }

      const approvalPlan = toApprovalPlan(approval);
      setPlan(approvalPlan);
      setPlanFile(approvalPlan.storeKey ? await fetchPlanFile(approvalPlan.storeKey) : null);
    } catch (err) {
      setError((err as Error).message ?? 'Failed to load plan details');
    } finally {
      setLoading(false);
    }
  }, [approvalId]);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = useCallback(
    async (status: 'APPROVED' | 'REJECTED', comment?: string) => {
      if (!plan?.reviewId) {
        setError('This approval has no review to act on');
        return;
      }
      await submitReviewDecision(plan.reviewId, {
        decision: status,
        ...(comment ? { comment } : {}),
      });
      await load();
    },
    [plan, load]
  );

  return { plan, planFile, loading, error, updateStatus };
}
